import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { MdOutlineArrowLeft } from "react-icons/md";
import * as Slider from "@radix-ui/react-slider";
import Tags from "../../components/Tags/Tags";
import RatingStar from "../../components/RatingStar/RatingStar";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../utils/axios/api";
import { categories } from "../../utils/category";
import { brands } from "../../utils/brand";
import { problemSolutions } from "../../utils/problem-solutions";
import { stageSubCategory } from "../../utils/stage-subcategory";
import styles from "./Search.module.css";
import { Filter } from "./types";

const filterData = {
  category: categories.map((c) => c.name),
  brand: brands.map((b) => b.name),
  problems: Array.from(problemSolutions, ([key, { solutions }]) => ({ problem: key, solutions })),
  stage: Array.from(stageSubCategory, ([key, { subcategory }]) => ({ stage: key, subcategory })),
};

export default function FilterPanel() {
  const { t } = useTranslation("translation", { keyPrefix: "Search" });
  const { t: tFilter } = useTranslation("translation", { keyPrefix: "filterNames" });
  const navigate = useNavigate();
  const [paramQuery] = useSearchParams();

  const search = paramQuery.get("name");
  const categoriesSelected = paramQuery.get("categories");
  const brandsSelected = paramQuery.get("brands");
  const problemsSelected = paramQuery.get("problems");
  const stagesSelected = paramQuery.get("stage");
  const solutionsSelected = paramQuery.get("solutions");
  const subcategorySelected = paramQuery.get("subcategories");
  const filterSelected = paramQuery.get("filter");
  const minSelected = paramQuery.get("min");
  const maxSelected = paramQuery.get("max");
  const starSelected = paramQuery.get("star");

  const [checkedItems, setCheckedItems] = useState<Filter[]>([]);
  const [filtersApplied, setFiltersApplied] = useState<Filter[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | undefined>();
  const [valuesRange, setValuesRange] = useState([0, 0]);
  const [minAnMaxValuesRange, setMinAnMaxValuesRange] = useState([0, 0]);
  const [isDownCategorys, setIsDownCategorys] = useState(false);
  const [isDownMarcas, setIsDownMarcas] = useState(false);
  const [isDownPreco, setIsDownPreco] = useState(false);
  const [isDownProblems, setIsDownProblems] = useState(false);
  const [isDownStage, setIsDownStage] = useState(false);

  useEffect(() => {
    api.get("/product/filters").then((res) => {
      const { min, max } = res.data;
      setValuesRange([min, max]);
      setMinAnMaxValuesRange([min, max]);
    });
  }, []);

  // Sync checkbox state from URL params
  useEffect(() => {
    const checkedItemsParam: Filter[] = [];
    if (categoriesSelected)
      categoriesSelected.split(",").forEach((c) => checkedItemsParam.push({ name: c, type: "categories" }));
    if (brandsSelected)
      brandsSelected.split(",").forEach((b) => checkedItemsParam.push({ name: b, type: "brands" }));
    if (problemsSelected)
      problemsSelected.split(",").forEach((p) => checkedItemsParam.push({ name: p, type: "problems" }));
    if (stagesSelected)
      stagesSelected.split(",").forEach((s) => checkedItemsParam.push({ name: s, type: "stage" }));
    if (solutionsSelected)
      solutionsSelected.split(",").forEach((s) => checkedItemsParam.push({ name: s, type: "solutions" }));
    if (subcategorySelected)
      subcategorySelected.split(",").forEach((s) => checkedItemsParam.push({ name: s, type: "subcategory" }));
    setCheckedItems(checkedItemsParam);
    setFiltersApplied(checkedItemsParam);
    if (starSelected !== null && starSelected !== undefined) setActiveIndex(Number(starSelected) - 1);
    else setActiveIndex(undefined);
    if (minSelected && maxSelected) setValuesRange([Number(minSelected), Number(maxSelected)]);
  }, [categoriesSelected, brandsSelected, problemsSelected, stagesSelected, solutionsSelected, subcategorySelected, starSelected, minSelected, maxSelected]);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    type: "categories" | "brands" | "problems" | "solutions" | "stage" | "subcategory",
    problemStage?: string
  ) => {
    const { name, checked } = event.target;
    if (checked) {
      if (type === "solutions") {
        setCheckedItems((prev) => {
          const aux = [...prev];
          const idx = aux.findIndex((item) => item.name === problemStage);
          if (idx < 0) return aux;
          if (aux[idx].problemSolution) aux[idx].problemSolution!.push(name);
          else aux[idx].problemSolution = [name];
          return aux;
        });
      } else if (type === "subcategory") {
        setCheckedItems((prev) => {
          const aux = [...prev];
          const idx = aux.findIndex((item) => item.name === problemStage);
          if (idx < 0) return aux;
          if (aux[idx].stageSub) aux[idx].stageSub!.push(name);
          else aux[idx].stageSub = [name];
          return aux;
        });
      } else {
        setCheckedItems((prev) => [...prev, { name, type }]);
      }
    } else {
      if (type === "solutions") {
        setCheckedItems((prev) => {
          const aux = [...prev];
          const idx = aux.findIndex((item) => item.name === problemStage);
          if (idx < 0) return aux;
          aux[idx].problemSolution = aux[idx].problemSolution
            ? aux[idx].problemSolution!.filter((f) => f !== name)
            : [];
          return aux;
        });
      } else if (type === "subcategory") {
        setCheckedItems((prev) => {
          const aux = [...prev];
          const idx = aux.findIndex((item) => item.name === problemStage);
          if (idx < 0) return aux;
          aux[idx].stageSub = aux[idx].stageSub
            ? aux[idx].stageSub!.filter((f) => f !== name)
            : [];
          return aux;
        });
      } else {
        setCheckedItems((prev) => prev.filter((f) => f.name !== name));
      }
    }
  };

  function removeFilter(name: string) {
    setFiltersApplied((prev) => prev.filter((item) => item.name !== name));
    setCheckedItems((prev) => prev.filter((item) => item.name !== name));
  }

  function handleClear() {
    setFiltersApplied([]);
    setCheckedItems([]);
    setActiveIndex(undefined);
    const newParams = new URLSearchParams(paramQuery.toString());
    newParams.delete("name");
    navigate(`?${newParams.toString()}`);
  }

  function handleFormSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const categoryList: string[] = [];
    const brandList: string[] = [];
    const problemList: string[] = [];
    const solutionList: string[] = [];
    const stageList: string[] = [];
    const subcategoryList: string[] = [];

    checkedItems.forEach((item) => {
      if (item.type === "brands") {
        brandList.push(item.name);
      } else if (item.type === "problems") {
        problemList.push(item.name);
        solutionList.push(item.problemSolution ? item.problemSolution.join("-") : "");
      } else if (item.type === "stage") {
        stageList.push(item.name);
        subcategoryList.push(item.stageSub ? item.stageSub.join("-") : "");
      } else if (item.type !== "solutions" && item.type !== "subcategory") {
        categoryList.push(item.name);
      }
    });

    const newParams = new URLSearchParams();
    if (search) newParams.set("name", search);
    if (filterSelected) newParams.set("filter", filterSelected);
    if (categoryList.length) newParams.set("categories", categoryList.join(","));
    if (brandList.length) newParams.set("brands", brandList.join(","));
    if (problemList.length) newParams.set("problems", problemList.join(","));
    if (solutionList.some((s) => s)) newParams.set("solutions", solutionList.join(","));
    if (stageList.length) newParams.set("stage", stageList.join(","));
    if (subcategoryList.some((s) => s)) newParams.set("subcategories", subcategoryList.join(","));

    const [min, max] = valuesRange;
    if (min !== minAnMaxValuesRange[0]) newParams.set("min", String(min));
    if (max !== minAnMaxValuesRange[1]) newParams.set("max", String(max));
    if (activeIndex !== undefined) newParams.set("star", String(activeIndex + 1));

    navigate(`/search/1?${newParams.toString()}`);
  }

  const onClickStar = (index: number) => {
    setActiveIndex((prev) => (prev === index ? undefined : index));
  };

  return (
    <form className={styles.filters} onSubmit={handleFormSubmit}>
      <h2>{t("searchFilters")}</h2>

      <button className={styles.applyButton} type="submit">
        <p>{t("applyFilters")}</p>
      </button>

      <button className={styles.clearButton} onClick={handleClear} type="button">
        <p>{t("cleanFilter")}</p>
      </button>

      <h3>{t("applyFilters")}</h3>
      <div className={styles.tags}>
        {filtersApplied.map((item, key) => (
          <Tags key={key} name={item.name} onClick={() => removeFilter(item.name)} />
        ))}
      </div>

      <div className={styles.horizontalLine} />

      {/* Categories */}
      <div className={styles.wrapperCategorys}>
        <div className={styles.titleCategory}>
          <h3>{t("categorys")}</h3>
          <MdOutlineArrowLeft
            size={30}
            color="#D8372C"
            className={isDownCategorys ? `${styles.arrow} ${styles.down}` : styles.arrow}
            onClick={() => setIsDownCategorys(!isDownCategorys)}
          />
        </div>
        {isDownCategorys &&
          filterData.category.map((item, key) => (
            <div key={key} className={styles.wrapperCheckbox}>
              <input
                className={styles.checkbox}
                type="checkbox"
                name={item}
                id={item}
                onChange={(e) => handleChange(e, "categories")}
                checked={checkedItems.some((f) => f.type === "categories" && f.name === item)}
              />
              <label htmlFor={item}>{tFilter(item, item)}</label>
            </div>
          ))}
      </div>

      <div className={styles.horizontalLine} />

      {/* Brands */}
      <div className={styles.wrapperCategorys}>
        <div className={styles.titleCategory}>
          <h3>{t("brands")}</h3>
          <MdOutlineArrowLeft
            size={30}
            color="#D8372C"
            className={isDownMarcas ? `${styles.arrow} ${styles.down}` : styles.arrow}
            onClick={() => setIsDownMarcas(!isDownMarcas)}
          />
        </div>
        {isDownMarcas &&
          filterData.brand.map((item, key) =>
            item === "Outro" ? undefined : (
              <div key={key} className={styles.wrapperCheckbox}>
                <input
                  className={styles.checkbox}
                  type="checkbox"
                  name={item}
                  id={item}
                  onChange={(e) => handleChange(e, "brands")}
                  checked={checkedItems.some((f) => f.type === "brands" && f.name === item)}
                />
                <label htmlFor={item}>{item}</label>
              </div>
            )
          )}
      </div>

      <div className={styles.horizontalLine} />

      {/* Problems & Stage */}
      <div className={styles.wrapperCategorys}>
        <div className={styles.titleCategory}>
          <h3>{t("problems")}</h3>
          <MdOutlineArrowLeft
            size={30}
            color="#D8372C"
            className={isDownProblems ? `${styles.arrow} ${styles.down}` : styles.arrow}
            onClick={() => setIsDownProblems(!isDownProblems)}
          />
        </div>
        {isDownProblems &&
          filterData.problems.map((item, key) => {
            const checkedIdx = checkedItems.findIndex(
              (f) => f.type === "problems" && f.name === item.problem
            );
            const checked = checkedIdx >= 0;
            return item.problem === "Outro" ? undefined : (
              <div key={key} style={{ display: "flex", flexDirection: "column" }}>
                <div className={styles.wrapperCheckbox}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    name={item.problem}
                    id={item.problem}
                    onChange={(e) => handleChange(e, "problems")}
                    checked={checked}
                  />
                  <label htmlFor={item.problem}>{tFilter(item.problem, item.problem)}</label>
                </div>
                {checked &&
                  problemSolutions.get(item.problem)?.solutions.map((solution) => (
                    <div
                      key={solution}
                      style={{ marginTop: 8, display: "flex", justifyContent: "flex-start", marginLeft: 24, alignItems: "center", gap: "0 8px" }}
                    >
                      <input
                        className={styles.checkbox}
                        type="checkbox"
                        name={solution}
                        id={solution}
                        onChange={(e) => handleChange(e, "solutions", item.problem)}
                        checked={
                          !checkedItems[checkedIdx].problemSolution
                            ? false
                            : checkedItems[checkedIdx].problemSolution!.some((f) => f === solution)
                        }
                      />
                      <label htmlFor={solution}>{tFilter(solution, solution)}</label>
                    </div>
                  ))}
              </div>
            );
          })}

        <div className={styles.horizontalLine} />

        <div className={styles.titleCategory}>
          <h3>{t("stage")}</h3>
          <MdOutlineArrowLeft
            size={30}
            color="#D8372C"
            className={isDownStage ? `${styles.arrow} ${styles.down}` : styles.arrow}
            onClick={() => setIsDownStage(!isDownStage)}
          />
        </div>
        {isDownStage &&
          filterData.stage.map((item, key) => {
            const checkedIdx = checkedItems.findIndex(
              (f) => f.type === "stage" && f.name === item.stage
            );
            const checked = checkedIdx >= 0;
            return item.stage === "Outro" ? undefined : (
              <div key={key} style={{ display: "flex", flexDirection: "column" }}>
                <div className={styles.wrapperCheckbox}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    name={item.stage}
                    id={item.stage}
                    onChange={(e) => handleChange(e, "stage")}
                    checked={checked}
                  />
                  <label htmlFor={item.stage}>{tFilter(item.stage, item.stage)}</label>
                </div>
                {checked &&
                  stageSubCategory.get(item.stage)?.subcategory.map((sub) => (
                    <div
                      key={sub}
                      style={{ marginTop: 8, display: "flex", justifyContent: "flex-start", marginLeft: 24, alignItems: "center", gap: "0 8px" }}
                    >
                      <input
                        className={styles.checkbox}
                        type="checkbox"
                        name={sub}
                        id={sub}
                        onChange={(e) => handleChange(e, "subcategory", item.stage)}
                        checked={
                          !checkedItems[checkedIdx].stageSub
                            ? false
                            : checkedItems[checkedIdx].stageSub!.some((f) => f === sub)
                        }
                      />
                      <label htmlFor={sub}>{tFilter(sub, sub)}</label>
                    </div>
                  ))}
              </div>
            );
          })}
      </div>

      <div className={styles.horizontalLine} />

      {/* Price range */}
      <div className={styles.wrapperCategorys}>
        <div className={styles.titleCategory}>
          <h3>{t("price")}</h3>
          <MdOutlineArrowLeft
            size={30}
            color="#D8372C"
            className={isDownPreco ? `${styles.arrow} ${styles.down}` : styles.arrow}
            onClick={() => setIsDownPreco(!isDownPreco)}
          />
        </div>
        {isDownPreco && (
          <>
            <Slider.Root
              className={styles.SliderRoot}
              value={valuesRange}
              onValueChange={(values) => setValuesRange(values)}
              min={minAnMaxValuesRange[0]}
              max={minAnMaxValuesRange[1]}
              step={1}
            >
              <Slider.Track className={styles.SliderTrack}>
                <Slider.Range className={styles.SliderRange} />
              </Slider.Track>
              <Slider.Thumb className={styles.SliderThumb} aria-label="Volume" />
              <Slider.Thumb className={styles.SliderThumb} aria-label="Volume" />
            </Slider.Root>
            <p>
              R${valuesRange[0]} - R${valuesRange[1]}
            </p>
          </>
        )}
      </div>

      <div className={styles.horizontalLine} />

      {/* Ratings */}
      <div className={styles.wrapperCategorys}>
        <div className={styles.titleCategory}>
          <h3>{t("avaliations")}</h3>
        </div>
        {[...new Array(5).keys()].map((index) => (
          <RatingStar
            key={`star_${index}`}
            onClick={() => onClickStar(index)}
            isActive={index <= activeIndex!}
          />
        ))}
      </div>
    </form>
  );
}
